/**
 * Профиль клиента для карточки стойки — из уже загруженных списков комплекса.
 */

import { getUserIdFromRequest } from './sectionClientsAggregate';
import { formatUserLabel, isPaidInPeriod } from './complexClients';

export function clientRowKey(row) {
  if (!row) return '';
  if (row.businessClientId != null && row.businessClientId !== '') return `bc-${row.businessClientId}`;
  if (row.userId != null && row.userId !== '') return `u-${row.userId}`;
  return '';
}

export function formatProfileDate(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function sectionTitleById(sections, id) {
  if (id == null || id === '') return '';
  const n = Number(id);
  const found = (sections || []).find((s) => Number(s.id != null ? s.id : s.Id) === n);
  if (found) return found.name || found.Name || `Секция #${n}`;
  return `Секция #${n}`;
}

function abonementSectionIds(abonement) {
  const ids = abonement && (abonement.sectionIds || abonement.SectionIds);
  return Array.isArray(ids) ? ids.map((x) => Number(x)).filter((x) => !Number.isNaN(x)) : [];
}

function requestStatusLabel(req) {
  const raw = req && (req.statusName || req.StatusName || req.status || req.Status);
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (n === 0) return 'Новая';
  if (n === 1) return 'В работе';
  if (n === 2) return 'Одобрена';
  if (n === 3) return 'Отклонена';
  const s = String(raw);
  if (/new/i.test(s)) return 'Новая';
  if (/progress/i.test(s)) return 'В работе';
  if (/approv/i.test(s)) return 'Одобрена';
  if (/reject/i.test(s)) return 'Отклонена';
  return s;
}

export function buildClientProfile({
  row,
  userMap = {},
  abonements = [],
  groupMembers = [],
  personalTrainings = [],
  requests = [],
  sections = [],
  now = new Date(),
} = {}) {
  if (!row) {
    return null;
  }
  const uidRaw = row.userId != null && row.userId !== '' ? Number(row.userId) : null;
  const userId = uidRaw != null && !Number.isNaN(uidRaw) ? uidRaw : null;
  const user = userId != null ? (userMap[userId] || userMap[String(userId)] || null) : null;
  const name = (user && formatUserLabel(user).split(' · ')[0])
    || row.name
    || (user && (user.name || user.Name))
    || 'Клиент';
  const phone =
    (user && (user.phoneNumber || user.PhoneNumber || user.phone))
    || row.phone
    || '';
  const email =
    (user && (user.email || user.Email))
    || row.email
    || '';
  const hasAccount = userId != null;
  const linked = row.linked === true || (hasAccount && row.businessClientId != null);

  const myAbonements = hasAccount
    ? (abonements || [])
      .filter((a) => Number(a.userId != null ? a.userId : a.UserId) === userId)
      .map((a) => {
        const ids = abonementSectionIds(a);
        const paid = a.isPaid === true || a.IsPaid === true;
        const active = isPaidInPeriod(a, now);
        return {
          id: a.id != null ? a.id : a.Id,
          dateFrom: formatProfileDate(a.dateFrom || a.DateFrom),
          dateTo: formatProfileDate(a.dateTo || a.DateTo),
          isPaid: paid,
          isActive: active,
          sectionNames: ids.length
            ? ids.map((id) => sectionTitleById(sections, id))
            : [],
        };
      })
    : [];
  const hasActiveAbonement = myAbonements.some((a) => a.isActive);
  let abonementStatus = 'Нет абонемента';
  if (!hasAccount) {
    abonementStatus = 'Нет аккаунта';
  } else if (hasActiveAbonement) {
    abonementStatus = 'Активен';
  } else if (myAbonements.some((a) => a.isPaid)) {
    abonementStatus = 'Истёк';
  } else if (myAbonements.length) {
    abonementStatus = 'Не оплачен';
  }

  const groups = hasAccount
    ? (groupMembers || [])
      .filter((m) => Number(m.userId != null ? m.userId : m.UserId) === userId)
      .map((m) => {
        const sectionId = m.sectionId != null ? m.sectionId : m.SectionId;
        const sectionName = m.sectionName || m.SectionName || sectionTitleById(sections, sectionId);
        const groupName = m.groupName || m.GroupName || 'Группа';
        return {
          id: m.id != null ? m.id : m.Id,
          sectionId,
          sectionName,
          groupName,
          label: [sectionName, groupName].filter(Boolean).join(' · '),
        };
      })
    : [];

  const sectionSet = new Set();
  groups.forEach((g) => {
    if (g.sectionName) sectionSet.add(g.sectionName);
  });
  myAbonements.forEach((a) => {
    (a.sectionNames || []).forEach((n) => {
      if (n) sectionSet.add(n);
    });
  });

  const personals = hasAccount
    ? (personalTrainings || [])
      .filter((p) => Number(p.userId != null ? p.userId : p.UserId) === userId)
      .map((p) => {
        const start = p.startAt || p.StartAt;
        const sectionId = p.sectionId != null ? p.sectionId : p.SectionId;
        return {
          id: p.id != null ? p.id : p.Id,
          startAt: start,
          startLabel: formatProfileDate(start),
          upcoming: start ? new Date(start).getTime() >= now.getTime() : false,
          sectionName: sectionTitleById(sections, sectionId),
        };
      })
      .sort((a, b) => {
        const ta = a.startAt ? new Date(a.startAt).getTime() : 0;
        const tb = b.startAt ? new Date(b.startAt).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 5)
    : [];

  const myRequests = hasAccount
    ? (requests || [])
      .filter((r) => getUserIdFromRequest(r) === userId)
      .map((r) => {
        const sectionId = r.sectionId != null ? r.sectionId : r.SectionId;
        return {
          id: r.id != null ? r.id : r.Id,
          status: requestStatusLabel(r),
          sectionName: r.sectionName || r.SectionName || sectionTitleById(sections, sectionId),
        };
      })
    : [];

  return {
    key: clientRowKey(row),
    userId,
    businessClientId: row.businessClientId != null ? row.businessClientId : null,
    name,
    phone,
    email,
    hasAccount,
    linked,
    hasActiveAbonement,
    abonementStatus,
    abonements: myAbonements,
    groups,
    sections: Array.from(sectionSet),
    personals,
    requests: myRequests,
  };
}
